import { useDispatch, useSelector } from 'react-redux'

import { RootState } from '../types'

import { Dictionary } from '../../utils/types'

import { ColumnSorting, Identifier, TableKey } from './types'
import { selectVisibleColumns, selectPagination, selectSorting, selectSelection } from './selectors'
import { TableActions } from './actions'

export function useTableColumnVisibility(tableKey: TableKey) {
  const dispatch = useDispatch()
  const visibleColumns = useSelector((state: RootState) => selectVisibleColumns(state, { tableKey }))

  return {
    visibleColumns,
    onSelectionChange: (visibleColumns: string[]) =>
      dispatch(TableActions.setVisibleColumns({ tableKey, visibleColumns }))
  }
}

export function useTablePagination(tableKey: TableKey, functionToCall?: (value: any) => void, value?: any) {
  const dispatch = useDispatch()
  const pagination = useSelector((state: RootState) => selectPagination(state, { tableKey }))

  return {
    pagination,
    onPaginationChange: async (current: number, pageSize: number, total: number) => {
      functionToCall && (await functionToCall(value))
      dispatch(TableActions.changePagination({ tableKey: tableKey, pagination: { current, pageSize, total } }))
    }
  }
}

export function useTableSorting(tableKey: TableKey, functionToCall?: (value: any) => void, value?: any) {
  const dispatch = useDispatch()
  const sorting = useSelector((state: RootState) => selectSorting(state, { tableKey }))

  return {
    sorting,
    onSortingChange: async (sorting: Dictionary<ColumnSorting>) => {
      functionToCall && (await functionToCall(value))
      dispatch(TableActions.setSorting({ tableKey: tableKey, sorting }))
    }
  }
}

export function useTableRowSelection(tableKey: TableKey) {
  const dispatch = useDispatch()
  const rowSelection = useSelector((state: RootState) => selectSelection(state, { tableKey }))

  return {
    rowSelection,
    onRowSelectionChange: (selection: Identifier[]) =>
      dispatch(TableActions.setSelection({ tableKey: tableKey, selection }))
  }
}

export function useTable(tableKey: TableKey) {
  const rowSelection = useTableRowSelection(tableKey)
  const sorting = useTableSorting(tableKey)
  const pagination = useTablePagination(tableKey)

  return {
    ...rowSelection,
    ...sorting,
    ...pagination
  }
}
